import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@aitisal/ui/retail-storefront/carousel";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ResponsiveImage from "../ResponsiveImage";

interface GalleryImage {
  originalImage?: string;
  image?: string;
  thumbnailImage?: string;
  variants?: {
    webp?: { [key: string]: string };
    jpeg?: { [key: string]: string };
  };
}

interface ProductGalleryProps {
  images?: GalleryImage[];
  title: string;
}

const PLACEHOLDER = "/images/img-placeholder.svg";

const fullSize = (img: GalleryImage) =>
  img.originalImage || img.image || img.thumbnailImage || PLACEHOLDER;

const thumbSize = (img: GalleryImage) =>
  img.thumbnailImage || img.image || img.originalImage || PLACEHOLDER;

const ProductGallery: React.FC<ProductGalleryProps> = ({
  images = [],
  title,
}) => {
  const slides: GalleryImage[] =
    images.length > 0 ? images : [{ image: PLACEHOLDER }];

  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState("50% 50%");
  const canHoverRef = useRef(false);

  useEffect(() => {
    canHoverRef.current = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;
  }, []);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    onSelect();
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const isLightboxOpen = lightboxIndex !== null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const stepLightbox = useCallback(
    (delta: number) => {
      setLightboxIndex((prev) =>
        prev === null ? prev : (prev + delta + slides.length) % slides.length,
      );
    },
    [slides.length],
  );

  useEffect(() => {
    if (!isLightboxOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowRight") stepLightbox(1);
      if (event.key === "ArrowLeft") stepLightbox(-1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isLightboxOpen, closeLightbox, stepLightbox]);

  const scrollToSlide = (index: number) => {
    carouselApi?.scrollTo(index, true);
  };

  const handleZoomMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canHoverRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin(`${x}% ${y}%`);
  };

  const renderThumb = (slide: GalleryImage, index: number) => (
    <button
      key={index}
      type="button"
      aria-label={`View image ${index + 1} of ${slides.length}`}
      aria-current={activeIndex === index}
      onClick={() => scrollToSlide(index)}
      className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 bg-gray-50 transition-all duration-200 cursor-pointer ${
        activeIndex === index
          ? "border-gray-900 opacity-100"
          : "border-transparent opacity-70 hover:opacity-100"
      }`}
    >
      <img
        src={thumbSize(slide)}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-full w-full object-cover"
      />
    </button>
  );

  const lightboxSlide =
    lightboxIndex !== null ? slides[lightboxIndex] : undefined;

  return (
    <div className="flex gap-4">
      <div className="hidden lg:flex flex-col gap-3 lg:max-h-[640px] lg:overflow-y-auto no-scrollbar">
        {slides.length > 1 && slides.map(renderThumb)}
      </div>

      <div className="min-w-0 flex-1">
        <Carousel
          opts={{ loop: slides.length > 1 }}
          setApi={setCarouselApi}
          className="group w-full"
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                <div
                  className={`relative aspect-square w-full overflow-hidden rounded-2xl bg-gray-50 ${
                    canHoverRef.current ? "cursor-zoom-in" : "cursor-pointer"
                  }`}
                  onClick={() => setLightboxIndex(index)}
                  onMouseEnter={() => {
                    if (canHoverRef.current) setIsZooming(true);
                  }}
                  onMouseLeave={() => setIsZooming(false)}
                  onMouseMove={handleZoomMove}
                >
                  <ResponsiveImage
                    imageObject={slide}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    alt={`${title} view ${index + 1}`}
                    className="absolute inset-0 h-full w-full"
                    style={{
                      height: "100%",
                      transformOrigin: zoomOrigin,
                      transform: isZooming ? "scale(1.8)" : "scale(1)",
                    }}
                    loading={index === 0 ? "eager" : "lazy"}
                    priority={index === 0}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {slides.length > 1 && (
            <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
              <span className="rounded-full bg-black/50 px-2.5 py-1 text-xs font-semibold text-white tabular-nums backdrop-blur-sm">
                {activeIndex + 1}/{slides.length}
              </span>
              <button
                type="button"
                aria-label="View full screen"
                onClick={() => setLightboxIndex(activeIndex)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-md backdrop-blur-sm transition hover:bg-white"
              >
                <Expand className="h-4 w-4" />
              </button>
            </div>
          )}

          {slides.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => carouselApi?.scrollPrev()}
                className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-900 opacity-0 shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-white group-hover:opacity-100 sm:flex"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={() => carouselApi?.scrollNext()}
                className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-900 opacity-0 shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-white group-hover:opacity-100 sm:flex"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </Carousel>

        {slides.length > 1 && (
          <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar lg:hidden">
            {slides.map(renderThumb)}
          </div>
        )}
      </div>

      {isLightboxOpen && lightboxSlide && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${title} image viewer`}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            type="button"
            aria-label="Close image viewer"
            autoFocus
            onClick={closeLightbox}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>

          {slides.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={(e) => {
                  e.stopPropagation();
                  stepLightbox(-1);
                }}
                className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:left-6"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={(e) => {
                  e.stopPropagation();
                  stepLightbox(1);
                }}
                className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-6"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white tabular-nums">
                {(lightboxIndex ?? 0) + 1} / {slides.length}
              </span>
            </>
          )}

          <img
            key={lightboxIndex ?? 0}
            src={fullSize(lightboxSlide)}
            alt={`${title} full view ${(lightboxIndex ?? 0) + 1}`}
            draggable={false}
            decoding="async"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[92vw] select-none object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
